import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Text } from 'src/design/atoms/Text';
import { Tabs } from 'src/design/molecules/Tabs';
import { useJobs } from 'src/hooks/useJobs';
import {
  TransmittalForm,
  TransmittalFormSection,
  TransmittalFormValues,
} from 'src/routes/Jobs/Transmittals/components/TransmittalForm';
import { NotificationsTab } from 'src/app/workflows/NotificationsTab';
import {
  HistoryList,
  TransmittalHistoryDto,
} from 'src/app/workflows/HistoryList';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { useTransmittal } from 'src/api/queries/transmittals/useTransmittalQueries';
import { TransmittalDto } from 'src/api/FiretrolApiClient';
import { Email } from 'src/app/workflows/Email';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { parseExternalContact } from 'src/app/workflows/parseExternalContact';

export const ViewTransmittal: React.FC = () => {
  const params = useParams<{ transmittalId: string }>();
  const navigate = useNavigate();
  const { selectedJob, users } = useJobs();
  const [tab, setTab] = React.useState<string>('details');
  const {
    data: transmittal,
    isLoading: loading,
    isError,
  } = useTransmittal(selectedJob?.id, params.transmittalId);
  const transmittalHistory = transmittal?.history ?? [];

  const formValues = React.useMemo<TransmittalFormValues | null>(() => {
    if (!transmittal) return null;
    return {
      ...transmittal,
      externalContact: parseExternalContact(transmittal.externalContact),
      filesToAdd: [],
      filesToRemove: [],
    };
  }, [transmittal]);

  if (!selectedJob || !params.transmittalId) return null;
  if (loading) return <Loader />;
  if (isError) return <Text c="red">Unable to find transmittal</Text>;
  if (!transmittal || !formValues)
    return <Text c="red">Error loading transmittal</Text>;

  return (
    <>
      <Header
        title={transmittal.transmittalNumber || 'No Transmittal Number'}
        breadcrumbProps={{
          items: jobBreadcrumbs(
            selectedJob,
            {
              text: 'Transmittals',
              href: `/jobs/${selectedJob.id}/transmittals`,
            },
            transmittal.transmittalNumber || 'No Transmittal Number',
          ),
        }}
        helpText="View and update this transmittal's details, documents, and notifications."
        actions={
          <SecondaryButton
            onClick={() => navigate(`/jobs/${selectedJob.id}/transmittals`)}
          >
            Cancel
          </SecondaryButton>
        }
      />
      <Tabs value={tab} onChange={(value) => setTab(value || 'details')}>
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="attachments">Documents</Tabs.Tab>
          <Tabs.Tab value="signed">Signed Documents</Tabs.Tab>
          <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
          <Tabs.Tab value="emails">Emails</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Box mt="md">
        <TransmittalForm
          initialValues={formValues}
          selectedJob={selectedJob}
          activeSection={tab as TransmittalFormSection}
          jobUsers={users}
        >
          {tab === 'notifications' && (
            <NotificationsTab
              resourceType="transmittal"
              entityNoun="transmittal"
              attachPdfLabel="Attach transmittal PDF"
              jobId={selectedJob.id}
              entityId={params.transmittalId}
              jobUsers={users}
              customerNumber={selectedJob.customerNumber?.trim() ?? ''}
            />
          )}

          {tab === 'history' && <HistoryList history={transmittalHistory} />}

          {tab === 'emails' && (
            <Email
              jobNumber={selectedJob.jobNumber}
              productType="TRANSMITTAL"
              productNumber={transmittal.transmittalNumber}
            />
          )}
        </TransmittalForm>
      </Box>
    </>
  );
};
